import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/use-auth";
import { apiFetch } from "@/lib/api";
import {
  Save, User, Mail, Shield, MessageSquare, Building2, Search,
  Star, Plus, Trash2, Bell, Smartphone, CheckCircle2, XCircle,
  Clock, CreditCard, AlertTriangle, CalendarClock, Banknote,
  PackageOpen, Repeat2, WifiOff, Users2,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ImageUpload } from "@/components/image-upload";
import { ChangePasswordCard } from "@/components/change-password-card";
import { useGetStudentGyms, useAddStudentGym, useRemoveStudentGym, useSetStudentPrimaryGym, useListPublicGyms } from "@workspace/api-client-react";

type ChannelPrefs = { email: boolean; sms: boolean; push: boolean };
type AllNotifPrefs = Record<string, ChannelPrefs>;

const DEFAULT_PREFS: ChannelPrefs = { email: true, sms: true, push: true };

type NotifGroup = {
  label: string;
  items: { type: string; label: string; description: string; icon: React.ReactNode }[];
};

const NOTIF_GROUPS: NotifGroup[] = [
  {
    label: "Sessions",
    items: [
      { type: "BOOKING_CONFIRMED", label: "Session Confirmed", description: "Your booking request was approved by the trainer", icon: <CheckCircle2 className="w-4 h-4 text-green-400" /> },
      { type: "BOOKING_REJECTED", label: "Session Declined", description: "A session request couldn't be accommodated", icon: <XCircle className="w-4 h-4 text-red-400" /> },
      { type: "BOOKING_CANCELLED", label: "Session Cancelled", description: "A confirmed session was cancelled", icon: <XCircle className="w-4 h-4 text-orange-400" /> },
      { type: "TIME_PROPOSAL_RECEIVED", label: "Time Proposed", description: "Your trainer suggested alternate session times", icon: <Clock className="w-4 h-4 text-blue-400" /> },
      { type: "SESSION_REMINDER", label: "Session Reminder", description: "Reminder before an upcoming session", icon: <CalendarClock className="w-4 h-4 text-primary" /> },
    ],
  },
  {
    label: "Payments & Packages",
    items: [
      { type: "PAYMENT_CONFIRMED", label: "Payment Confirmed", description: "A payment was processed successfully", icon: <Banknote className="w-4 h-4 text-green-400" /> },
      { type: "PACKAGE_LOW_CREDITS", label: "Low Credits Warning", description: "Your session package is running low", icon: <PackageOpen className="w-4 h-4 text-amber-400" /> },
      { type: "PACKAGE_EXPIRING_SOON", label: "Package Expiring", description: "Your package is about to expire", icon: <CreditCard className="w-4 h-4 text-orange-400" /> },
      { type: "MEMBERSHIP_RENEWAL_REMINDER", label: "Renewal Reminder", description: "Your membership is coming up for renewal", icon: <Repeat2 className="w-4 h-4 text-blue-400" /> },
      { type: "MEMBERSHIP_PAYMENT_FAILED", label: "Payment Failed", description: "A payment couldn't be processed", icon: <Banknote className="w-4 h-4 text-red-400" /> },
    ],
  },
  {
    label: "Alerts",
    items: [
      { type: "NOSHOW_FLAGGED", label: "No-Show Alert", description: "A session was flagged as a no-show", icon: <AlertTriangle className="w-4 h-4 text-orange-400" /> },
      { type: "BOOKING_AVAILABLE", label: "Waitlist Spot Open", description: "A trainer from your waitlist now has availability", icon: <Users2 className="w-4 h-4 text-primary" /> },
      { type: "MEMBERSHIP_CANCELLED", label: "Membership Cancelled", description: "Your gym membership has been cancelled", icon: <WifiOff className="w-4 h-4 text-red-400" /> },
    ],
  },
];

function getPrefs(all: AllNotifPrefs, type: string): ChannelPrefs {
  const saved = all[type];
  return { ...DEFAULT_PREFS, ...saved };
}

export default function StudentProfile() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    bio: "",
    smsEnabled: false,
    emailEnabled: true,
    pushEnabled: true,
  });

  const [notifPrefs, setNotifPrefs] = useState<AllNotifPrefs>({});
  const [notifPrefsSaving, setNotifPrefsSaving] = useState(false);
  const [notifPrefsLoaded, setNotifPrefsLoaded] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        phone: (user as any).phone || "",
        bio: (user as any).bio || "",
        smsEnabled: (user as any).smsEnabled ?? false,
        emailEnabled: (user as any).emailEnabled ?? true,
        pushEnabled: (user as any).pushEnabled ?? true,
      });
      if (!notifPrefsLoaded) {
        setNotifPrefs((user as any).notificationPreferences ?? {});
        setNotifPrefsLoaded(true);
      }
    }
  }, [user]);

  function setChannel(type: string, channel: keyof ChannelPrefs, value: boolean) {
    setNotifPrefs(prev => ({
      ...prev,
      [type]: { ...getPrefs(prev, type), [channel]: value },
    }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiFetch("/auth/profile", { method: "PUT", body: JSON.stringify(form) });
      await refreshUser();
      toast({ title: "Profile updated" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNotifPrefs() {
    setNotifPrefsSaving(true);
    try {
      await apiFetch("/auth/notification-preferences", {
        method: "PATCH",
        body: JSON.stringify({ preferences: notifPrefs }),
      });
      await refreshUser();
      toast({ title: "Notification preferences saved" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setNotifPrefsSaving(false);
    }
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12 max-w-2xl">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">My Profile</h1>
          <p className="text-muted-foreground">Manage your account information</p>
        </div>

        {/* Personal Info */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display text-white flex items-center gap-2">
              <User className="w-5 h-5" /> Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
              <ImageUpload
                currentUrl={user?.avatarUrl}
                onUpload={() => { refreshUser(); }}
                uploadEndpoint="/uploads/avatar"
                label="Change Photo"
                shape="circle"
                size="md"
                fallback={
                  <div className="w-full h-full bg-primary/20 flex items-center justify-center text-2xl font-bold text-primary">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                }
              />
              <div>
                <p className="text-lg font-bold text-white">{user?.firstName} {user?.lastName}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {user?.email}</p>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3" /> {user?.role} · Member since {user?.createdAt ? format(new Date(user.createdAt), "MMM yyyy") : "N/A"}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-white mb-2 block">First Name</label>
                <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="bg-background border-white/10" />
              </div>
              <div>
                <label className="text-sm font-medium text-white mb-2 block">Last Name</label>
                <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="bg-background border-white/10" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 123-4567" className="bg-background border-white/10" />
            </div>

            <div>
              <label className="text-sm font-medium text-white mb-2 block">Bio</label>
              <Textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} placeholder="Tell us about yourself..." rows={3} className="bg-background border-white/10" />
            </div>

            {/* Global channel master switches */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-white">Notification Channels</p>
              <p className="text-xs text-muted-foreground mb-3">Master switches — disabling a channel turns it off for all alerts below</p>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                  <div>
                    <Label htmlFor="email-toggle" className="text-sm font-medium text-white cursor-pointer">Email</Label>
                    <p className="text-xs text-muted-foreground">Confirmations, reminders, receipts</p>
                  </div>
                </div>
                <Switch id="email-toggle" checked={form.emailEnabled} onCheckedChange={(checked) => setForm({ ...form, emailEnabled: checked })} />
              </div>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-4 h-4 text-green-400 shrink-0" />
                  <div>
                    <Label htmlFor="sms-toggle" className="text-sm font-medium text-white cursor-pointer">SMS</Label>
                    <p className="text-xs text-muted-foreground">Booking confirmations and day-of reminders</p>
                  </div>
                </div>
                <Switch id="sms-toggle" checked={form.smsEnabled} onCheckedChange={(checked) => setForm({ ...form, smsEnabled: checked })} />
              </div>
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-white/5 border border-white/5">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-4 h-4 text-purple-400 shrink-0" />
                  <div>
                    <Label htmlFor="push-toggle" className="text-sm font-medium text-white cursor-pointer">Push Notifications</Label>
                    <p className="text-xs text-muted-foreground">Instant alerts on the mobile app</p>
                  </div>
                </div>
                <Switch id="push-toggle" checked={form.pushEnabled} onCheckedChange={(checked) => setForm({ ...form, pushEnabled: checked })} />
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/80">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Profile"}
            </Button>
          </CardContent>
        </Card>

        {/* Per-type Notification Preferences */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-primary" /> Notification Preferences
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Fine-tune which channels each alert uses. Global channel switches above must also be on for a channel to fire.
            </p>

            {/* Column header */}
            <div className="flex items-center gap-3 px-1 mb-1">
              <div className="flex-1" />
              <div className="flex items-center gap-5 pr-0.5">
                <span className="w-8 text-center">
                  <Mail className="w-3.5 h-3.5 text-blue-400 mx-auto" />
                </span>
                <span className="w-8 text-center">
                  <MessageSquare className="w-3.5 h-3.5 text-green-400 mx-auto" />
                </span>
                <span className="w-8 text-center">
                  <Smartphone className="w-3.5 h-3.5 text-purple-400 mx-auto" />
                </span>
              </div>
            </div>

            {NOTIF_GROUPS.map((group) => (
              <div key={group.label} className="space-y-1">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest px-1 mb-2">{group.label}</p>
                <div className="rounded-xl border border-white/5 overflow-hidden divide-y divide-white/5">
                  {group.items.map(({ type, label, description, icon }) => {
                    const prefs = getPrefs(notifPrefs, type);
                    return (
                      <div key={type} className="flex items-center gap-3 px-4 py-3 bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                        <div className="shrink-0">{icon}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">{label}</p>
                          <p className="text-xs text-muted-foreground truncate">{description}</p>
                        </div>
                        <div className="flex items-center gap-5 shrink-0">
                          <div className="w-8 flex justify-center">
                            <Switch
                              checked={prefs.email}
                              onCheckedChange={(v) => setChannel(type, "email", v)}
                              className="scale-75"
                            />
                          </div>
                          <div className="w-8 flex justify-center">
                            <Switch
                              checked={prefs.sms}
                              onCheckedChange={(v) => setChannel(type, "sms", v)}
                              className="scale-75"
                            />
                          </div>
                          <div className="w-8 flex justify-center">
                            <Switch
                              checked={prefs.push}
                              onCheckedChange={(v) => setChannel(type, "push", v)}
                              className="scale-75"
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            <Button onClick={handleSaveNotifPrefs} disabled={notifPrefsSaving} className="w-full">
              <Save className="w-4 h-4 mr-2" />
              {notifPrefsSaving ? "Saving..." : "Save Notification Preferences"}
            </Button>
          </CardContent>
        </Card>

        <ManageHomeGyms />
        <ChangePasswordCard />
      </div>
    </AppLayout>
  );
}

function ManageHomeGyms() {
  const { toast } = useToast();
  const [showAddGym, setShowAddGym] = useState(false);
  const [gymSearch, setGymSearch] = useState("");

  const { data: studentGyms, isLoading, refetch } = useGetStudentGyms();
  const { data: allGyms } = useListPublicGyms();
  const addGymMutation = useAddStudentGym();
  const removeGymMutation = useRemoveStudentGym();
  const setPrimaryMutation = useSetStudentPrimaryGym();

  const availableGyms = useMemo(() => {
    if (!allGyms || !studentGyms) return [];
    const memberGymIds = new Set(studentGyms.map((sg) => sg.gymId));
    let available = allGyms.filter((g) => !memberGymIds.has(g.id));
    if (gymSearch.trim()) {
      const q = gymSearch.toLowerCase();
      available = available.filter(
        (g) =>
          g.name.toLowerCase().includes(q) ||
          (g.address && g.address.toLowerCase().includes(q))
      );
    }
    return available;
  }, [allGyms, studentGyms, gymSearch]);

  const handleAddGym = async (gymId: string) => {
    try {
      await addGymMutation.mutateAsync({ data: { gymId } });
      await refetch();
      toast({ title: "Gym added" });
      setShowAddGym(false);
      setGymSearch("");
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleRemoveGym = async (gymId: string) => {
    try {
      await removeGymMutation.mutateAsync({ gymId });
      await refetch();
      toast({ title: "Gym removed" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  const handleSetPrimary = async (gymId: string) => {
    try {
      await setPrimaryMutation.mutateAsync({ gymId });
      await refetch();
      toast({ title: "Primary gym updated" });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    }
  };

  return (
    <Card className="glass-panel border-white/5">
      <CardHeader>
        <CardTitle className="text-xl font-display text-white flex items-center gap-2">
          <Building2 className="w-5 h-5" /> My Home Gyms
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : !studentGyms || studentGyms.length === 0 ? (
          <div className="text-center py-6">
            <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
            <p className="text-sm text-muted-foreground mb-3">No home gyms yet. Add one to see trainer schedules.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {studentGyms.map((sg) => (
              <div
                key={sg.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-white/[0.02]"
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                  {sg.gym?.logoUrl ? (
                    <img src={sg.gym.logoUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Building2 className="w-4 h-4 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {sg.gym?.name || "Gym"}
                    {sg.isPrimary && (
                      <span className="ml-2 text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">Primary</span>
                    )}
                  </p>
                  {sg.gym?.address && (
                    <p className="text-xs text-muted-foreground truncate">{sg.gym.address}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {!sg.isPrimary && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSetPrimary(sg.gymId)}
                      disabled={setPrimaryMutation.isPending}
                      className="text-muted-foreground hover:text-primary h-8 w-8 p-0"
                      title="Set as primary"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemoveGym(sg.gymId)}
                    disabled={removeGymMutation.isPending}
                    className="text-muted-foreground hover:text-red-400 hover:bg-red-400/10 h-8 w-8 p-0"
                    title="Remove gym"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAddGym ? (
          <div className="space-y-3 p-4 rounded-xl border border-white/10 bg-white/[0.02]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search gyms..."
                value={gymSearch}
                onChange={(e) => setGymSearch(e.target.value)}
                className="pl-10 bg-background border-white/10 h-10 rounded-lg"
                autoFocus
              />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1">
              {availableGyms.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No gyms available</p>
              ) : (
                availableGyms.map((gym) => (
                  <button
                    key={gym.id}
                    type="button"
                    onClick={() => handleAddGym(gym.id)}
                    className="w-full p-2 rounded-lg border border-white/5 text-left hover:bg-white/[0.04] transition-colors flex items-center gap-2"
                  >
                    <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {gym.logoUrl ? (
                        <img src={gym.logoUrl} alt="" className="w-full h-full object-cover rounded-md" />
                      ) : (
                        <Building2 className="w-3 h-3 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate">{gym.name}</p>
                      {gym.address && (
                        <p className="text-xs text-muted-foreground truncate">{gym.address}</p>
                      )}
                    </div>
                    <Plus className="w-4 h-4 text-primary shrink-0" />
                  </button>
                ))
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowAddGym(false); setGymSearch(""); }}
              className="w-full border-white/10 hover:bg-white/5 text-sm"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            variant="outline"
            onClick={() => setShowAddGym(true)}
            className="w-full border-white/10 hover:bg-white/5"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Home Gym
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
